import { Button, ButtonMode, Modal as LibModal, Loader } from '@creditsights/styleguide-web';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useSubmit from './hooks/useSubmit';
import styles from './Modal.module.scss';
import Step1 from './steps/Step1';
import Step21 from './steps/Step21';
import Step22 from './steps/Step22';
import Step3 from './steps/Step3';
import Step4 from './steps/Step4';
import { FieldValues, Step, AssetClass } from './types';

export interface BaseProps {
  onClose: () => void;
}

const Modal = ({ onClose }: BaseProps): React.ReactElement => {
  const [step, setStep] = useState<Step>('1');
  const { mutateAsync, isError, error } = useSubmit();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const methods = useForm<FieldValues>({
    mode: 'onChange',
    defaultValues: {
      files: [],
    },
  });

  const isStep3 = ['3.1', '3.2', '3.3'].includes(step);

  const handleSubmit = methods.handleSubmit(async (payload) => {
    const form = new FormData();

    form.append('assetClass', payload.assetClass || '');
    form.append('date', payload.date || '');
    form.append('transactionType', payload.transactionType || '');
    form.append('borrower', payload.borrower || '');
    form.append('message', payload.message || '');

    payload.files.forEach((file) => {
      form.append('files[]', file);
    });

    try {
      await mutateAsync(form);
      setStep('4');
    } catch (e) {
      wrapperRef.current?.scroll?.(0, 0);
    }
  });

  const handleChangeType = useCallback((assetClass: AssetClass) => {
    methods.setValue('assetClass', assetClass);
    methods.setValue('date', undefined);

    switch (assetClass) {
      case 'bond':
        methods.setValue('transactionType', undefined);
        setStep('2.2');
        break;
      case 'loan':
        setStep('2.1');
        break;
      case 'other':
        setStep('3.3');
        break;
      default:
        break;
    }
  }, []);

  const headerTitle = useMemo(() => {
    const mainTitle = 'Request CR Research';
    switch (step) {
      case '2.1':
      case '3.1':
        return `${mainTitle} | Loan`;
      case '2.2':
      case '3.2':
        return `${mainTitle} | Bond`;
      case '3.3':
        return `${mainTitle} | Other`;
      default:
        return mainTitle;
    }
  }, [step]);

  const goToPrevStep = useCallback(() => {
    methods.setValue('borrower', undefined);
    methods.setValue('message', undefined);
    methods.setValue('files', []);

    switch (step) {
      case '3.1':
        setStep('2.1');
        break;
      case '3.2':
        setStep('2.2');
        break;
      default:
        methods.reset();
        setStep('1');
        break;
    }
  }, [step]);

  const goToNextStep = useCallback(() => {
    if (step === '2.1') {
      setStep('3.1');
    } else if (step === '2.2') {
      setStep('3.2');
    } else if (isStep3) {
      handleSubmit();
    } else {
      setStep('1');
    }
  }, [step, isStep3, handleSubmit]);

  const handleOneMore = useCallback(() => {
    methods.reset();
    setStep('1');
  }, []);

  return (
    <LibModal
      dataTestId="document-upload-modal"
      className={styles.modal}
      headerTitle={headerTitle}
      onClose={onClose}
      open
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit}>
          <div className={styles.modalScrollWrapper} ref={wrapperRef}>
            {isError ? (
              <div className={styles.modalGlobalError}>
                Error: {error?.response?.data?.message || 'connection issue'} -{' '}
                <span className={styles.modalLink} onClick={() => handleSubmit()}>
                  try again
                </span>
              </div>
            ) : null}
            <div>
              {step === '1' ? <Step1 onChangeType={handleChangeType} /> : null}
              {step === '2.1' ? <Step21 /> : null}
              {step === '2.2' ? <Step22 /> : null}
              {isStep3 ? <Step3 /> : null}
              {step === '4' ? <Step4 onOneMore={handleOneMore} /> : null}
            </div>
            <div>
              For Covenant Review questions or comments, email{' '}
              <a href="mailto:research@covenantreview.com">research@covenantreview.com</a>
            </div>
          </div>
          <div className={styles.modalFooter}>
            {step === '1' || step === '4' ? (
              <Button mode={ButtonMode.SECONDARY} onClick={onClose}>
                {step === '1' ? 'Cancel' : 'Close'}
              </Button>
            ) : (
              <>
                <Button mode={ButtonMode.SECONDARY} onClick={goToPrevStep}>
                  Back
                </Button>
                <Button
                  mode={ButtonMode.PRIMARY}
                  onClick={goToNextStep}
                  disabled={isStep3 && !methods.formState.isValid}
                >
                  {isStep3 ? 'Submit request' : 'Next'}
                </Button>
              </>
            )}
          </div>
          {methods.formState.isSubmitting ? (
            <div className={styles.modalLoader}>
              <Loader text="" />
            </div>
          ) : null}
        </form>
      </FormProvider>
    </LibModal>
  );
};

export default Modal;
